import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdFileDownload, MdRefresh, MdSync } from 'react-icons/md';
import LeadDebugButton from './LeadDebugButton';
import ExportLeadsModal from './ExportLeadsModal';

// Onglet « En cours » : leads importés chez Batiweb, en attente de leur réponse. Le cron serveur
// relève leur statut tous les jours à 9h ; les boutons permettent de le faire tout de suite.

interface ImportedLead {
    _id: string;
    createdAt: string;
    contact: { firstname: string | null; lastname: string | null; email: string | null; phone: string | null; zipcode: string | null };
    seller: string | null;
    industry: string | null;
    subIndustry: string | null;
    boughtAfterwards: boolean;
    // Attribué ensuite à une offre locked du waterfall.
    waterfall: { offerId: string; reason: string; dispatchedAt: string } | null;
    // Enregistrés à l'approbation d'un lead vendu : prix payé par Batiweb et prix minimum du vendeur.
    buyerPrice: number | null;
    leadPrice: number | null;
    // Prix Batiweb d'après la grille du worktype, affiché tant que le lead n'est pas approuvé.
    expectedBuyerPrice: number;
    paidAt: string | null;
    helloArtisan: {
        status: 'pending' | 'sold' | 'unsold';
        worktypeId?: number;
        worktypeLabel?: string;
        importedAt?: string;
        importedBy?: string;
        expectedResponseAt?: string;
        lastCheckedAt?: string;
        statusLabel?: string;
        resolvedAt?: string;
        // Sans réponse au bout de 5 jours ouvrés : traité comme Annulé.
        timedOutAt?: string;
        lastCode?: number;
        lastMessage?: string;
    };
}

const STATUS_FILTERS = [
    { key: 'pending', label: 'En cours' },
    { key: 'sold', label: 'Vendus' },
    { key: 'unsold', label: 'Refusés (Bourse ou waterfall)' },
    { key: 'all', label: 'Tous' },
] as const;
type StatusFilter = typeof STATUS_FILTERS[number]['key'];

const STATUS_BADGES: Record<ImportedLead['helloArtisan']['status'], { label: string; className: string }> = {
    pending: { label: 'En attente', className: 'bg-blue-100 text-blue-700' },
    sold: { label: 'Vendu', className: 'bg-green-100 text-green-700' },
    unsold: { label: 'Annulé', className: 'bg-gray-100 text-gray-600' },
};

const formatEuro = (value: number) => `${value.toFixed(2).replace('.', ',')} €`;

const formatDate = (value?: string) =>
    value ? new Date(value).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

export default function ImportedLeadsTab() {
    const [leads, setLeads] = useState<ImportedLead[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
    const [refreshingId, setRefreshingId] = useState<string | null>(null);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState('');
    const [banner, setBanner] = useState('');
    const [showExport, setShowExport] = useState(false);

    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

    const fetchLeads = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const res = await axios.get(`${CONFIG.apiUrl}/admin/batiweb/imported-leads`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                params: { status: statusFilter },
            });
            setLeads(res.data.leads);
        } catch (e: any) {
            setError(e.response?.data?.message || 'Impossible de charger les leads importés.');
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        fetchLeads();
    }, [fetchLeads]);

    const handleRefresh = async (lead: ImportedLead) => {
        try {
            setRefreshingId(lead._id);
            setError('');
            const res = await axios.post(`${CONFIG.apiUrl}/admin/batiweb/leads/${lead._id}/refresh-status`, {}, { headers });
            const label = res.data.helloArtisan?.statusLabel || res.data.helloArtisan?.lastMessage || 'pas de réponse';
            const approval =
                res.data.approval === 'approved'
                    ? ` — approuvé (Batiweb €${res.data.buyerPrice}, vendeur €${res.data.leadPrice}), à payer depuis Leads > Approuvés`
                    : '';
            const waterfall = res.data.waterfall?.ok ? ` — attribué au waterfall (${res.data.waterfall.buyerEmail})` : '';
            setBanner(`Statut Batiweb : ${label}${approval}${waterfall}`);
            fetchLeads();
        } catch (e: any) {
            setError(e.response?.data?.message || 'Erreur lors de la vérification du statut.');
        } finally {
            setRefreshingId(null);
        }
    };

    const handleSyncAll = async () => {
        try {
            setSyncing(true);
            setError('');
            const res = await axios.post(`${CONFIG.apiUrl}/admin/batiweb/sync-statuses`, {}, { headers });
            const s = res.data;
            setBanner(`${s.checked} lead(s) vérifié(s) : ${s.sold} vendu(s), ${s.unsold} refusé(s)${s.timedOut ? ` (dont ${s.timedOut} sans réponse)` : ''}, ${s.pending} toujours en attente, ${s.failed} erreur(s)${s.reconciled ? `, ${s.reconciled} envoi(s) interrompu(s) régularisé(s)` : ''}${s.approved ? `, ${s.approved} lead(s) approuvé(s) à payer depuis Leads > Approuvés` : ''}${s.waterfall?.dispatched ? `, ${s.waterfall.dispatched} attribué(s) au waterfall` : ''}.`);
            fetchLeads();
        } catch (e: any) {
            setError(e.response?.data?.message || 'Erreur lors de la vérification des statuts.');
        } finally {
            setSyncing(false);
        }
    };

    const now = Date.now();

    // Totaux des leads listés qui ont un prix (vendus et approuvés).
    const totals = useMemo(() => {
        const priced = leads.filter((l) => l.buyerPrice != null && l.leadPrice != null);
        const buyer = priced.reduce((sum, l) => sum + (l.buyerPrice as number), 0);
        const seller = priced.reduce((sum, l) => sum + (l.leadPrice as number), 0);
        return { count: priced.length, buyer, seller, margin: buyer - seller };
    }, [leads]);

    return (
        <>
            <div className="mb-4 flex flex-wrap items-center gap-2">
                {STATUS_FILTERS.map((filter) => (
                    <button
                        key={filter.key}
                        onClick={() => setStatusFilter(filter.key)}
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            statusFilter === filter.key ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-600 dark:bg-navy-700 dark:text-gray-300'
                        }`}
                    >
                        {filter.label}
                    </button>
                ))}
                <div className="ml-auto flex gap-2">
                    <button
                        onClick={() => setShowExport(true)}
                        className="flex items-center gap-1 rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-semibold text-navy-700 hover:bg-gray-200 dark:bg-navy-700 dark:text-white"
                    >
                        <MdFileDownload />
                        Exporter
                    </button>
                    <button
                        onClick={fetchLeads}
                        disabled={loading}
                        className="flex items-center gap-1 rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-semibold text-navy-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-navy-700 dark:text-white"
                    >
                        <MdRefresh />
                        Actualiser
                    </button>
                    <button
                        onClick={handleSyncAll}
                        disabled={syncing}
                        className="flex items-center gap-1 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
                    >
                        <MdSync />
                        {syncing ? 'Vérification...' : 'Vérifier tous les statuts maintenant'}
                    </button>
                </div>
            </div>

            <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
                Le statut des leads en attente est vérifié automatiquement tous les jours à 9h. « Validé » → vendu ; « Annulé », ou toujours
                « Non traité » 5 jours ouvrés après l'import → refusé : attribué à une offre du waterfall si l'une correspond, sinon remis sur
                la Bourse du Lead ; « Non traité » avant ce délai → revérifié le lendemain.
            </p>

            {showExport && <ExportLeadsModal onClose={() => setShowExport(false)} />}

            {error && <div className="mb-4 rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">{error}</div>}
            {banner && <div className="mb-4 rounded-lg bg-green-100 p-3 text-sm text-green-700 dark:bg-green-900/30 dark:text-green-400">{banner}</div>}

            {loading ? (
                <div className="py-10 text-center text-gray-400">Chargement...</div>
            ) : leads.length === 0 ? (
                <div className="py-10 text-center text-gray-400">Aucun lead.</div>
            ) : (
                <div className="overflow-x-auto">
                    <div className="mb-2 flex flex-wrap gap-x-4 text-xs text-gray-400">
                        <span>{leads.length} lead(s)</span>
                        {totals.count > 0 && (
                            <span>
                                {totals.count} vendu(s) avec prix : Batiweb {formatEuro(totals.buyer)} · vendeurs {formatEuro(totals.seller)} ·{' '}
                                <span className={`font-semibold ${totals.margin < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                    marge {totals.margin >= 0 ? '+' : ''}{formatEuro(totals.margin)}
                                </span>
                            </span>
                        )}
                    </div>
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-navy-600">
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Lead</th>
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Produit leadrs</th>
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Métier Batiweb</th>
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Importé le</th>
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Réponse attendue</th>
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Statut Batiweb</th>
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Prix</th>
                                <th className="pb-3 text-xs font-bold uppercase text-gray-500"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {leads.map((lead) => {
                                const ha = lead.helloArtisan;
                                const badge = ha.timedOutAt ? { label: 'Sans réponse', className: 'bg-orange-100 text-orange-700' } : STATUS_BADGES[ha.status];
                                const overdue = ha.status === 'pending' && !!ha.expectedResponseAt && new Date(ha.expectedResponseAt).getTime() < now;
                                return (
                                    <tr key={lead._id} className="border-b border-gray-100 align-top dark:border-navy-700">
                                        <td className="py-3 pr-4">
                                            <div className="flex items-center gap-1.5 font-semibold text-navy-700 dark:text-white">
                                                {[lead.contact.firstname, lead.contact.lastname].filter(Boolean).join(' ') || '—'}
                                                <LeadDebugButton leadId={lead._id} onEdited={fetchLeads} />
                                            </div>
                                            <div className="text-xs text-gray-500">{lead.contact.email || '—'} · {lead.contact.zipcode || 'CP ?'}</div>
                                            <div className="font-mono text-xs text-gray-400">{lead._id}</div>
                                        </td>
                                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                                            {lead.subIndustry || '—'}
                                            <div className="text-xs text-gray-400">{lead.industry || ''}</div>
                                        </td>
                                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                                            {ha.worktypeLabel || '—'}
                                            {ha.worktypeId != null && <span className="ml-1 text-xs text-gray-400">({ha.worktypeId})</span>}
                                        </td>
                                        <td className="py-3 pr-4 text-xs text-gray-500 whitespace-nowrap">
                                            {formatDate(ha.importedAt)}
                                            <div className="text-gray-400">{ha.importedBy === 'auto' ? 'automatique' : ha.importedBy}</div>
                                        </td>
                                        <td className="py-3 pr-4 text-xs whitespace-nowrap">
                                            <span className={overdue ? 'font-semibold text-red-600' : 'text-gray-500'}>{formatDate(ha.expectedResponseAt)}</span>
                                            {overdue && <div className="text-red-600">En retard</div>}
                                        </td>
                                        <td className="py-3 pr-4">
                                            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${badge.className}`}>{badge.label}</span>
                                            <div className="mt-1 text-xs text-gray-500">
                                                {ha.statusLabel || 'Pas encore vérifié'}
                                                {ha.lastCheckedAt && <span className="text-gray-400"> · vérifié le {formatDate(ha.lastCheckedAt)}</span>}
                                            </div>
                                            {ha.lastMessage && <div className="text-xs text-orange-600">{ha.lastMessage}</div>}
                                            {ha.timedOutAt && <div className="text-xs text-orange-600">Expiré le {formatDate(ha.timedOutAt)} (5 jours ouvrés)</div>}
                                            {lead.boughtAfterwards && <div className="text-xs text-green-600">Racheté ensuite sur la Bourse</div>}
                                            {lead.waterfall && <div className="text-xs text-purple-600">Attribué au waterfall le {formatDate(lead.waterfall.dispatchedAt)}</div>}
                                        </td>
                                        <td className="py-3 pr-4 font-mono text-xs whitespace-nowrap">
                                            {ha.status === 'sold' && lead.buyerPrice != null && lead.leadPrice != null ? (
                                                <>
                                                    <div className="text-gray-600 dark:text-gray-300">A : {formatEuro(lead.buyerPrice)}</div>
                                                    <div className="text-gray-600 dark:text-gray-300">V : {formatEuro(lead.leadPrice)}</div>
                                                    <div className={`font-semibold ${lead.buyerPrice - lead.leadPrice < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                        {lead.buyerPrice - lead.leadPrice >= 0 ? '+' : ''}{formatEuro(lead.buyerPrice - lead.leadPrice)}
                                                    </div>
                                                    <div className={`font-sans ${lead.paidAt ? 'text-gray-400' : 'text-orange-600'}`}>{lead.paidAt ? 'Vendeur payé' : 'Vendeur à payer'}</div>
                                                </>
                                            ) : ha.status === 'unsold' || lead.boughtAfterwards || lead.waterfall ? (
                                                <span className="text-gray-400">—</span>
                                            ) : (
                                                <div className="text-gray-400" title="Prix Batiweb d'après la grille ; le prix vendeur est fixé à l'approbation (prix minimum du vendeur)">
                                                    A : ≈ {formatEuro(lead.expectedBuyerPrice)}
                                                    <div className="font-sans">{ha.status === 'sold' ? 'pas encore approuvé' : 'attendu'}</div>
                                                </div>
                                            )}
                                        </td>
                                        <td className="py-3">
                                            {ha.status === 'pending' && (
                                                <button
                                                    onClick={() => handleRefresh(lead)}
                                                    disabled={refreshingId === lead._id || syncing}
                                                    className="flex items-center gap-1 whitespace-nowrap rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-semibold text-navy-700 hover:bg-gray-200 disabled:opacity-40 dark:bg-navy-700 dark:text-white"
                                                >
                                                    <MdSync />
                                                    {refreshingId === lead._id ? 'Vérification...' : 'Vérifier le statut'}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}
